/**
 * User Activity Page (Vera-wide)
 *
 * Cross-Vera admin view of who has signed into Vera and which areas they've
 * visited, across Sales, Ops, and Catalog. Reads from vba_useractivity.
 * Gated by the global Admin role.
 */

import { useMemo, useState } from 'react';
import {
  makeStyles,
  Title2,
  Body1,
  Caption1,
  Spinner,
  Card,
  Dropdown,
  Option,
  Badge,
  Button,
  Input,
} from '@fluentui/react-components';
import {
  PersonRegular,
  CalendarRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { Navigate } from 'react-router-dom';
import { COLORS } from '@shared/ui';
import { useAuth } from '@shared/providers/AuthProvider';
import { hasGlobalAdmin } from '@shared/auth';
import {
  useRecentUserActivity,
  userActivityApi,
  type UserActivity,
  type UserActivitySummary,
} from '@shared/entities/user-activity';

type TimeWindow = '24h' | '7d' | '30d' | 'custom';
type AppFilter = 'all' | 'sales' | 'ops' | 'catalog' | 'vera-shell';

const WINDOW_HOURS: Record<Exclude<TimeWindow, 'custom'>, number> = {
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
};

const WINDOW_LABEL: Record<TimeWindow, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  custom: 'Custom range',
};

const APP_LABEL: Record<AppFilter, string> = {
  all: 'All Vera apps',
  sales: 'Sales',
  ops: 'Ops',
  catalog: 'Catalog',
  'vera-shell': 'Shell (launcher / admin)',
};

function todayLocalISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function daysAgoLocalISO(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function startOfDayUtcISO(localDate: string): string {
  // Interpret the date as local-midnight, send to API as ISO.
  const [y, m, d] = localDate.split('-').map(Number);
  if (!y || !m || !d) return new Date(0).toISOString();
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

function endOfDayUtcISO(localDate: string): string {
  const [y, m, d] = localDate.split('-').map(Number);
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

function csvEscape(value: unknown): string {
  if (value === undefined || value === null) return '';
  let s = String(value);
  // Neutralize CSV-injection formula execution in Excel/Sheets when a cell
  // starts with =, +, -, or @. Prefix with a single quote so the spreadsheet
  // treats the value as literal text instead of evaluating it as a formula.
  if (s.length > 0 && /^[=+\-@]/.test(s)) {
    s = "'" + s;
  }
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(filename: string, header: string[], rows: unknown[][]) {
  const lines = [header.map(csvEscape).join(',')];
  for (const row of rows) lines.push(row.map(csvEscape).join(','));
  // BOM so Excel opens UTF-8 cleanly.
  const blob = new Blob(['﻿' + lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function timestampSlug(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

const useStyles = makeStyles({
  page: { padding: '24px', maxWidth: '1400px' },
  header: { marginBottom: '20px' },
  subtitle: { display: 'block', color: COLORS.textSecondary, marginTop: '8px' },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '1fr 420px',
    gap: '16px',
    alignItems: 'flex-start',
  },
  card: { padding: '0', overflow: 'hidden' },
  cardHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    fontSize: '14px',
    fontWeight: 600,
    color: COLORS.textPrimary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  cardHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  customRange: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  cardBody: { padding: '8px 0' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: `2px solid ${COLORS.border}`,
    fontWeight: 600,
    color: COLORS.textSecondary,
    fontSize: '12px',
    backgroundColor: COLORS.bgBase,
    position: 'sticky',
    top: 0,
  },
  td: {
    padding: '8px 12px',
    borderBottom: `1px solid ${COLORS.borderSubtle}`,
    verticalAlign: 'top',
  },
  row: {
    cursor: 'pointer',
    ':hover': { backgroundColor: COLORS.bgBase },
  },
  rowSelected: {
    backgroundColor: COLORS.bgElevated,
  },
  userName: { fontWeight: 600, color: COLORS.textPrimary },
  userEmail: { fontSize: '11px', color: COLORS.textSecondary },
  areasCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  areaChip: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: COLORS.bgSurface,
    color: COLORS.textPrimary,
    border: `1px solid ${COLORS.borderSubtle}`,
  },
  numCell: {
    fontFamily: "'JetBrains Mono', Consolas, monospace",
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  empty: {
    padding: '32px',
    textAlign: 'center' as const,
    color: COLORS.textSecondary,
  },
  timeline: {
    maxHeight: '560px',
    overflowY: 'auto',
  },
  timelineRow: {
    padding: '8px 16px',
    borderBottom: `1px solid ${COLORS.borderSubtle}`,
    display: 'grid',
    gridTemplateColumns: '110px 1fr',
    gap: '12px',
    alignItems: 'baseline',
  },
  timeCol: { fontSize: '11px', color: COLORS.textSecondary, whiteSpace: 'nowrap' },
  eventCol: { fontSize: '13px' },
  eventKind: { fontWeight: 600, marginRight: '8px' },
  eventPath: { fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: '11px', color: COLORS.textMuted },
  selectUserHint: {
    padding: '32px',
    textAlign: 'center' as const,
    color: COLORS.textMuted,
    fontSize: '13px',
  },
});

function formatRelative(iso: string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatTimestamp(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

function eventLabel(type: string): string {
  switch (type) {
    case 'login':
      return 'Signed in';
    case 'logout':
      return 'Signed out';
    case 'area_visit':
      return 'Visited';
    case 'page_view':
      return 'Viewed';
    default:
      return type;
  }
}

function eventBadgeColor(type: string): 'brand' | 'success' | 'informative' | 'subtle' {
  switch (type) {
    case 'login':
      return 'success';
    case 'logout':
      return 'subtle';
    case 'area_visit':
    case 'page_view':
      return 'informative';
    default:
      return 'subtle';
  }
}

function appBadgeColor(app: string): 'brand' | 'success' | 'informative' | 'warning' | 'subtle' {
  switch (app) {
    case 'sales':
      return 'brand';
    case 'ops':
      return 'warning';
    case 'catalog':
      return 'informative';
    case 'vera-shell':
      return 'subtle';
    default:
      return 'subtle';
  }
}

export function UserActivityPage() {
  const styles = useStyles();
  const { roles } = useAuth();
  const [windowKey, setWindowKey] = useState<TimeWindow>('7d');
  const [customFrom, setCustomFrom] = useState<string>(() => daysAgoLocalISO(7));
  const [customTo, setCustomTo] = useState<string>(() => todayLocalISO());
  const [appFilter, setAppFilter] = useState<AppFilter>('all');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { sinceIso, untilIso } = useMemo(() => {
    if (windowKey === 'custom') {
      return {
        sinceIso: customFrom ? startOfDayUtcISO(customFrom) : undefined,
        untilIso: customTo ? endOfDayUtcISO(customTo) : undefined,
      };
    }
    return {
      sinceIso: new Date(Date.now() - WINDOW_HOURS[windowKey] * 60 * 60 * 1000).toISOString(),
      untilIso: undefined as string | undefined,
    };
  }, [windowKey, customFrom, customTo]);

  const { data: allEvents, isLoading } = useRecentUserActivity(sinceIso, untilIso);

  const events = useMemo<UserActivity[]>(
    () =>
      appFilter === 'all'
        ? (allEvents ?? [])
        : (allEvents ?? []).filter((e) => e.app === appFilter),
    [allEvents, appFilter],
  );

  const summaries = useMemo<UserActivitySummary[]>(
    () => userActivityApi.summarize(events),
    [events],
  );

  const selectedSummary = useMemo(
    () => summaries.find((s) => s.userObjectId === selectedUserId) ?? null,
    [summaries, selectedUserId],
  );

  const selectedEvents = useMemo(
    () =>
      selectedUserId
        ? events.filter((e) => e.userObjectId === selectedUserId)
        : [],
    [events, selectedUserId],
  );

  const exportSummariesCsv = () => {
    const header = [
      'User',
      'Email',
      'Object ID',
      'Logins',
      'Last Login',
      'Last Seen',
      'Total Events',
      'Areas Visited',
    ];
    const rows = summaries.map((s) => [
      s.userName,
      s.userEmail,
      s.userObjectId,
      s.loginCount,
      s.lastLoginAt ?? '',
      s.lastSeenAt,
      s.totalEvents,
      s.areasVisited.map((a) => `${a.area}:${a.count}`).join('; '),
    ]);
    downloadCsv(`vera-user-activity-summary-${appFilter}-${timestampSlug()}.csv`, header, rows);
  };

  const exportTimelineCsv = () => {
    if (!selectedSummary) return;
    const header = ['Timestamp', 'App', 'Event Type', 'Area', 'Path', 'Session ID'];
    const rows = selectedEvents.map((e) => [
      e.createdOn,
      e.app,
      e.eventType,
      e.area ?? '',
      e.path ?? '',
      e.sessionId ?? '',
    ]);
    const userSlug = (selectedSummary.userEmail || selectedSummary.userName || 'user')
      .replace(/[^a-z0-9._-]+/gi, '-')
      .toLowerCase();
    downloadCsv(`vera-user-activity-${userSlug}-${timestampSlug()}.csv`, header, rows);
  };

  if (!hasGlobalAdmin(roles)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title2>User Activity</Title2>
        <Body1 className={styles.subtitle}>
          Who has signed into Vera and which areas they&apos;ve visited, across Sales, Ops, and Catalog. Admin access only.
        </Body1>
      </div>

      <div className={styles.controls}>
        <CalendarRegular style={{ fontSize: '16px', color: COLORS.textSecondary }} />
        <Dropdown
          size="small"
          value={WINDOW_LABEL[windowKey]}
          selectedOptions={[windowKey]}
          onOptionSelect={(_, data) => setWindowKey(data.optionValue as TimeWindow)}
          style={{ minWidth: '160px' }}
        >
          <Option value="24h">Last 24 hours</Option>
          <Option value="7d">Last 7 days</Option>
          <Option value="30d">Last 30 days</Option>
          <Option value="custom">Custom range</Option>
        </Dropdown>
        {windowKey === 'custom' && (
          <div className={styles.customRange}>
            <Input
              type="date"
              size="small"
              value={customFrom}
              max={customTo || todayLocalISO()}
              onChange={(_, data) => setCustomFrom(data.value)}
              aria-label="From date"
            />
            <Caption1 style={{ color: COLORS.textMuted }}>to</Caption1>
            <Input
              type="date"
              size="small"
              value={customTo}
              min={customFrom}
              max={todayLocalISO()}
              onChange={(_, data) => setCustomTo(data.value)}
              aria-label="To date"
            />
          </div>
        )}
        <Dropdown
          size="small"
          value={APP_LABEL[appFilter]}
          selectedOptions={[appFilter]}
          onOptionSelect={(_, data) => {
            setAppFilter(data.optionValue as AppFilter);
            setSelectedUserId(null);
          }}
          style={{ minWidth: '200px' }}
        >
          <Option value="all">All Vera apps</Option>
          <Option value="sales">Sales</Option>
          <Option value="ops">Ops</Option>
          <Option value="catalog">Catalog</Option>
          <Option value="vera-shell">Shell (launcher / admin)</Option>
        </Dropdown>
        {!isLoading && (
          <Caption1 style={{ color: COLORS.textMuted }}>
            {summaries.length} {summaries.length === 1 ? 'user' : 'users'} · {events.length} events
          </Caption1>
        )}
      </div>

      <div className={styles.layout}>
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardHeaderTitle}>
              <PersonRegular style={{ fontSize: '18px' }} />
              User Activity
            </span>
            <Button
              size="small"
              appearance="subtle"
              icon={<ArrowDownloadRegular />}
              onClick={exportSummariesCsv}
              disabled={summaries.length === 0}
            >
              Export CSV
            </Button>
          </div>
          <div className={styles.cardBody}>
            {isLoading ? (
              <div className={styles.empty}>
                <Spinner size="small" label="Loading activity..." />
              </div>
            ) : summaries.length === 0 ? (
              <div className={styles.empty}>
                No activity in this window yet.
              </div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>User</th>
                    <th className={styles.th} style={{ textAlign: 'right' }}>Logins</th>
                    <th className={styles.th}>Last login</th>
                    <th className={styles.th}>Last seen</th>
                    <th className={styles.th}>Areas visited</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map((s) => {
                    const selected = s.userObjectId === selectedUserId;
                    return (
                      <tr
                        key={s.userObjectId}
                        className={`${styles.row} ${selected ? styles.rowSelected : ''}`}
                        onClick={() =>
                          setSelectedUserId(selected ? null : s.userObjectId)
                        }
                      >
                        <td className={styles.td}>
                          <div className={styles.userName}>{s.userName || '(unknown)'}</div>
                          <div className={styles.userEmail}>{s.userEmail}</div>
                        </td>
                        <td className={`${styles.td} ${styles.numCell}`}>{s.loginCount}</td>
                        <td className={styles.td}>
                          {s.lastLoginAt ? formatRelative(s.lastLoginAt) : '—'}
                        </td>
                        <td className={styles.td}>{formatRelative(s.lastSeenAt)}</td>
                        <td className={styles.td}>
                          <div className={styles.areasCell}>
                            {s.areasVisited.length === 0 ? (
                              <span style={{ color: COLORS.textMuted }}>—</span>
                            ) : (
                              s.areasVisited.slice(0, 6).map((a) => (
                                <span key={a.area} className={styles.areaChip}>
                                  {a.area} · {a.count}
                                </span>
                              ))
                            )}
                            {s.areasVisited.length > 6 && (
                              <span className={styles.areaChip} style={{ opacity: 0.6 }}>
                                +{s.areasVisited.length - 6}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardHeaderTitle}>
              <CalendarRegular style={{ fontSize: '18px' }} />
              {selectedSummary ? `${selectedSummary.userName || 'User'} — events` : 'Event timeline'}
            </span>
            <Button
              size="small"
              appearance="subtle"
              icon={<ArrowDownloadRegular />}
              onClick={exportTimelineCsv}
              disabled={!selectedSummary || selectedEvents.length === 0}
            >
              Export CSV
            </Button>
          </div>
          <div className={styles.cardBody}>
            {!selectedSummary ? (
              <div className={styles.selectUserHint}>
                Select a user on the left to see their event timeline.
              </div>
            ) : selectedEvents.length === 0 ? (
              <div className={styles.empty}>No events for this user in the selected window.</div>
            ) : (
              <div className={styles.timeline}>
                {selectedEvents.map((e) => (
                  <div key={e.id} className={styles.timelineRow}>
                    <div className={styles.timeCol}>{formatTimestamp(e.createdOn)}</div>
                    <div className={styles.eventCol}>
                      <Badge
                        appearance="filled"
                        color={eventBadgeColor(e.eventType)}
                        size="small"
                        style={{ marginRight: '8px' }}
                      >
                        {eventLabel(e.eventType)}
                      </Badge>
                      {appFilter === 'all' && e.app && (
                        <Badge
                          appearance="outline"
                          color={appBadgeColor(e.app)}
                          size="small"
                          style={{ marginRight: '8px' }}
                        >
                          {e.app}
                        </Badge>
                      )}
                      {e.area && <span className={styles.eventKind}>{e.area}</span>}
                      {e.path && <span className={styles.eventPath}>{e.path}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default UserActivityPage;
